import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { ChartOfAccountDto } from "@/features/ledger/api/dto";
import { ChartOfAccountRowActions } from "@/features/ledger/components/chart-of-accounts/chart-of-account-row-actions";
import { ACCOUNT_TYPES, type AccountType } from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type ChartOfAccountRow = ChartOfAccountDto & { id: string };

const TYPE_VARIANTS: Record<
  AccountType,
  "default" | "secondary" | "outline" | "warning"
> = {
  ASSET: "default",
  LIABILITY: "warning",
  EQUITY: "secondary",
  REVENUE: "default",
  EXPENSE: "outline",
};

/**
 * Colonnes du plan de comptes (`POST /ChartOfAccounts/all`). Filtres :
 * `code`, `label`, `businessLine` (texte) et `accountType` (select).
 * Codes et identifiants en `NoTranslate`.
 */
export const chartOfAccountsColumns: ColumnsClarFactory<ChartOfAccountRow> = (
  navigate
) => [
  {
    id: "code",
    accessorKey: "code",
    header: t("ledger.accounts.columns.code"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "label",
    accessorKey: "label",
    header: t("ledger.accounts.columns.label"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "accountType",
    accessorKey: "accountType",
    header: t("ledger.accounts.columns.accountType"),
    cell: ({ getValue }) => {
      const type = getValue() as AccountType | undefined;
      return type ? (
        <NoTranslate as="span">
          <Badge variant={TYPE_VARIANTS[type] ?? "outline"}>{type}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: ACCOUNT_TYPES.map((value) => ({ value, label: value })),
    },
  },
  {
    id: "businessLine",
    accessorKey: "businessLine",
    header: t("ledger.accounts.columns.businessLine"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "isCommissionAccount",
    accessorKey: "isCommissionAccount",
    header: t("ledger.accounts.columns.isCommissionAccount"),
    cell: ({ getValue }) =>
      getValue() ? (
        <Badge variant="secondary">{t("ledger.accounts.commission")}</Badge>
      ) : (
        "—"
      ),
    meta: { type: "denied" },
  },
  {
    id: "currency",
    accessorKey: "currency",
    header: t("ledger.accounts.columns.currency"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "isActive",
    accessorKey: "isActive",
    header: t("ledger.accounts.columns.isActive"),
    cell: ({ getValue }) => (
      <Badge variant={getValue() ? "success" : "secondary"}>
        {getValue() ? t("ledger.common.active") : t("ledger.common.inactive")}
      </Badge>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <ChartOfAccountRowActions account={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
